import axios from "axios";
import { useState } from "react";
import { ApiConfig } from "../services/api-config";
import CookieUtils from "../utils/cookie-utils";
import { BaseResponse } from "../models/response/base-response";

const FILE_TYPES = {
  IMAGE: {
    types: ["image/jpeg", "image/jpg", "image/png"],
    code: "1",
  },
  PDF: {
    types: ["application/pdf"],
    code: "2",
  },
  VIDEO: {
    types: ["video/mp4", "video/quicktime"],
    code: "3",
  },
} as const;

const useUpload = <T>(uploadUrl: string, projectId = ApiConfig.PROJECT_ID.MEDIA_SERVICE) => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [baseResponse, setBaseResponse] = useState<BaseResponse<T>>();
  const finalToken = CookieUtils.getCurrentUser().access_token ?? "";

  
  const uploadFiles = async (files: FileList): Promise<BaseResponse<T> | void> => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    // Create a new FormData object and append each file
    const formData = new FormData();
    const types: string[] = [];

    Array.from(files).forEach((file) => {
      formData.append("files", file);

      const fileType = Object.values(FILE_TYPES).find((type) =>
        type.types.some((allowedType) => allowedType === file.type)
      );

      types.push(fileType?.code ?? "1");
    });
    formData.append("types", types.join(","));

    try {
      // Upload files via axios
      const response = await axios.post(`${ApiConfig.API_URL}/api/${uploadUrl}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(finalToken && {
            Authorization: `Bearer ${finalToken}`,
          }),
          ...(projectId && { "x-svc-id": projectId }),
          platform: "1", // WEB = 1, MOBILE = 2,
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total ?? 1;
          const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
          setUploadProgress(percentCompleted);
        },
      });

      // If upload is successful, reset progress
      setBaseResponse(response.data);
      setIsUploading(false);
      setUploadProgress(100);
      return response.data;
    } catch (error: unknown) {
      setIsUploading(false);
      let errorMessage = "";
      if (axios.isCancel(error)) {
        console.error("Request cancelled", error.message);
        errorMessage = error.message ?? "Request cancelled";
      } else {
        errorMessage = error instanceof Error ? error.message : "An unknown error occurred";
      }
      setUploadError(errorMessage);
    }
  };

  return {
    baseResponse,
    uploadFiles,
    isUploading,
    uploadProgress,
    uploadError,
  };
};
export default useUpload;
